import { ThirdPartyConnectionStatus } from '$constants'
import { ThirdPartyConnectionInfo } from '$models'
import { mercuryApi } from '$api'
import { LoadedError } from './loadedError'

/**
 * Converts an HTTP status code to a ThirdPartyConnectionStatus enum value
 * @param status - HTTP status code (200 = Connected, 404 = Disconnected, others = Unknown)
 * @returns ThirdPartyConnectionStatus enum value
 */
export function httpStatusToConnectionStatus(
  status?: number
): ThirdPartyConnectionStatus {
  if (!status) {
    return ThirdPartyConnectionStatus.Unknown
  }
  return status == 200
    ? ThirdPartyConnectionStatus.Connected
    : status == 404
    ? ThirdPartyConnectionStatus.Disconnected
    : ThirdPartyConnectionStatus.Unknown
}

/**
 * Checks the connection status of an external system by making an API call
 * and converting the HTTP response status to a ThirdPartyConnectionInfo object
 * @param system - The external system name (e.g., 'xero', 'square')
 * @param authModule - The auth module (e.g., 'Stock', 'Time')
 * @returns Promise resolving to ThirdPartyConnectionInfo or null if error
 */
export async function getExternalSystemConnectionInfo(
  system: string,
  authModule: string
): Promise<ThirdPartyConnectionInfo | null> {
  try {
    const connectionInfo = await mercuryApi
      .get<ThirdPartyConnectionInfo>(
        `/connections/${system}?module=${authModule}`
      )
      .then((response) => ({ status: 200, data: response }))
      .catch((error) => {
        if (error instanceof LoadedError) {
          return { status: error.status, data: null }
        }
        return null
      })

    if (!connectionInfo) {
      return {
        connectionStatus: ThirdPartyConnectionStatus.Unknown,
        thirdPartyDisplayMetadata: {},
      }
    }

    return {
      connectionStatus: httpStatusToConnectionStatus(connectionInfo.status),
      thirdPartyDisplayMetadata:
        connectionInfo.data?.thirdPartyDisplayMetadata ?? {},
    }
  } catch (error) {
    return null
  }
}
